import { projectSettingSource, type ProjectScopedServerSettingKey } from "@afar-research/afar";

import type { UnifiedSettings } from "@afar-research/contracts/settings";
import { useCallback, useMemo } from "react";

import {
  mergeEnvironmentSettings,
  splitUnifiedSettingsPatch,
  useClientSettings,
  useUpdateClientSettings,
} from "../../hooks/useSettings";
import { serverEnvironment } from "../../state/server";
import { useAtomCommand } from "../../state/use-atom-command";
import { toastManager } from "../ui/toast";
import { useOptionalSettingsScope, useSettingsScope } from "./SettingsScopeContext";
import {
  planSettingsOverrideClear,
  planSettingsWrite,
  runSettingsWritePlan,
  type SettingsWritePlan,
} from "./settingsWritePlan";

/**
 * The settings a row shows for the current selection: the scope's server-side
 * answer merged with this client's own preferences, so one page can carry both
 * without the reader knowing which store backs a key.
 */
export function useScopedSettings<T = UnifiedSettings>(
  selector?: (settings: UnifiedSettings) => T,
): T {
  const { displaySettings } = useSettingsScope();
  const clientSettings = useClientSettings();
  const merged = useMemo(
    () => mergeEnvironmentSettings(displaySettings, clientSettings),
    [clientSettings, displaySettings],
  );
  return useMemo(() => (selector ? selector(merged) : (merged as T)), [merged, selector]);
}

/**
 * Where the keys' answers come from at a project scope: the project's own
 * override, its Environment, or a mix when the keys disagree. Environment
 * scopes always read as inherited — there is nothing above them to inherit
 * from, so the row shows no chain.
 */
export function useScopedSettingSource(keys: readonly ProjectScopedServerSettingKey[]) {
  const context = useOptionalSettingsScope();
  return useMemo(() => {
    if (context === null || context.scope.kind !== "project") return "inherited" as const;
    const representative = context.scope.members[0];
    if (representative === undefined) return "inherited" as const;
    const settings = context.settingsByEnvironment.get(representative.environmentId);
    if (settings === undefined) return "inherited" as const;
    return projectSettingSource(settings, representative.id, keys);
  }, [context, keys]);
}

function useRunPlan() {
  const persist = useAtomCommand(serverEnvironment.updateSettings, { reportFailure: false });
  return useCallback(
    (plan: SettingsWritePlan) => {
      if (plan.refusal !== null) {
        toastManager.add({
          type: "warning",
          title: "Setting not saved",
          description: plan.refusal,
        });
        return;
      }
      void runSettingsWritePlan(plan, async ({ environmentId, patch }) => {
        const result = await persist({ environmentId, input: { patch } });
        return result._tag === "Success";
      }).then(({ savedEnvironmentIds, failedEnvironmentIds }) => {
        if (failedEnvironmentIds.length === 0) return;
        const names = failedEnvironmentIds.join(", ");
        toastManager.add({
          type: "error",
          title:
            savedEnvironmentIds.length > 0 ? "Saved on some environments" : "Setting not saved",
          description:
            savedEnvironmentIds.length > 0
              ? `Could not update ${names}. The others saved the change.`
              : `Could not update ${names}.`,
        });
      });
    },
    [persist],
  );
}

/**
 * Writes a unified patch at the current scope. Client-only keys are this
 * client's own preference and never fan out; the server keys go to every
 * Environment the scope covers, as a project override when one is selected.
 */
export function useUpdateScopedSettings() {
  const { scope, settingsByEnvironment } = useSettingsScope();
  const updateClientSettings = useUpdateClientSettings();
  const run = useRunPlan();
  return useCallback(
    (patch: Partial<UnifiedSettings>) => {
      const { serverPatch, clientPatch } = splitUnifiedSettingsPatch(patch);
      if (Object.keys(clientPatch).length > 0) updateClientSettings(clientPatch);
      if (Object.keys(serverPatch).length === 0) return;
      run(planSettingsWrite({ scope, patch: serverPatch, settingsByEnvironment }));
    },
    [run, scope, settingsByEnvironment, updateClientSettings],
  );
}

/**
 * Stop overriding `keys` so the projects in scope inherit their Environment
 * again. Rows also render outside the settings layout — provider cards,
 * dialogs — where there is no scope and so nothing to clear.
 */
export function useClearScopedSettings() {
  const context = useOptionalSettingsScope();
  const run = useRunPlan();
  return useCallback(
    (keys: readonly ProjectScopedServerSettingKey[]) => {
      if (context === null) return;
      run(
        planSettingsOverrideClear({
          scope: context.scope,
          keys,
          settingsByEnvironment: context.settingsByEnvironment,
        }),
      );
    },
    [context, run],
  );
}
